<template>
  <v-card color="white" :loading="loading" :disabled="loading">
    <v-card-title class="justify-center" style="background-color: #5E676E;">
      <div class="white--text text-center">線上預約管道與模式設定</div>
    </v-card-title>

    <v-card-text class="py-8">
      <div class="d-flex align-center flex-wrap">
        <div class="text-subtitle-1 font-weight-bold black--text">
          客製化編輯預約訊息
        </div>
        <v-tooltip bottom max-width="360">
          <template v-slot:activator="{ on, attrs }">
            <v-icon small color="grey-5" class="ml-2" v-bind="attrs" v-on="on">
              mdi-information-outline
            </v-icon>
          </template>
          <span>未啟用客製化時使用公版訊息，由小幫手負擔；啟用客製化後，簡訊會依實際字數與付款方設定扣點。</span>
        </v-tooltip>
      </div>
      <div class="mt-2 text-body-2 grey--text text--darken-1">
        全院統一，不分醫師。每 70 字計算 1 封，最多 280 字；LINE 發送不消耗簡訊點數。
      </div>

      <v-sheet outlined rounded class="mt-5 pa-4">
        <div class="d-flex align-center flex-wrap">
          <div class="text-body-1 font-weight-bold black--text">
            客製化簡訊付款方
          </div>
          <v-tooltip bottom max-width="360">
            <template v-slot:activator="{ on, attrs }">
              <v-icon small color="grey-5" class="ml-2" v-bind="attrs" v-on="on">
                mdi-information-outline
              </v-icon>
            </template>
            <span>僅客製化內容且實際使用簡訊發送時適用；公版內容仍由小幫手負擔。</span>
          </v-tooltip>
          <v-spacer />
          <v-btn
            small
            depressed
            color="primary"
            :loading="payerSaving"
            :disabled="!isPayerDirty"
            @click="savePayerSetting"
          >
            儲存付款方
          </v-btn>
        </div>
        <v-radio-group
          v-model="payerType"
          row
          hide-details
          class="mt-2"
          @change="payerSuccessMessage = ''"
        >
          <v-radio label="使用診所簡訊點數" value="1" />
          <v-radio label="使用醫師簡訊點數" value="2" />
        </v-radio-group>
        <v-alert
          v-if="payerSuccessMessage"
          dense
          text
          type="success"
          class="mt-3 mb-0"
        >
          {{ payerSuccessMessage }}
        </v-alert>
      </v-sheet>

      <v-expansion-panels v-model="activePanel" accordion class="mt-5">
        <online-appointment-message-template-panel
          v-for="templateConfig in templateConfigs"
          :key="templateConfig.key"
          :template-config="templateConfig"
          :template-state="templateStates[templateConfig.key]"
          :clinic-name="previewClinicName"
          :saving="savingKey === templateConfig.key"
          :max-characters="maxCharacters"
          @update-content="updateContent"
          @switch-mode="handleSwitchMode"
          @editor-click="handleEditorClick"
          @cancel="cancel"
          @save="saveSetting"
        />
      </v-expansion-panels>
    </v-card-text>

    <v-dialog v-model="quotaDialog" max-width="460">
      <v-card class="px-6 py-6">
        <div class="d-flex justify-center">
          <v-avatar color="orange lighten-5" size="56">
            <v-icon color="orange darken-2">mdi-credit-card-outline</v-icon>
          </v-avatar>
        </div>
        <v-card-title class="justify-center text-center font-weight-bold">
          需要儲值簡訊點數才能使用客製化功能
        </v-card-title>
        <v-card-text class="text-center text-body-1">
          {{ quotaDialogMessage }}
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn outlined color="grey-5" @click="quotaDialog = false">
            取消
          </v-btn>
          <v-btn
            color="primary"
            depressed
            :href="$enum.DENTCO_CS_LINE"
            target="_blank"
          >
            聯繫客服儲值
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import {
  getOnlineAppointmentTemplateAPI,
  updateOnlineAppointmentTemplateBulkAPI
} from "@/api";
import OnlineAppointmentMessageTemplatePanel from "./OnlineAppointmentMessageTemplatePanel.vue";
import {
  getOnlineAppointmentTokenLabel,
  MAX_ONLINE_APPOINTMENT_MESSAGE_CHARACTERS,
  ONLINE_APPOINTMENT_MESSAGE_MODES,
  ONLINE_APPOINTMENT_TEMPLATE_KEYS,
  ONLINE_APPOINTMENT_MESSAGE_TEMPLATES,
  renderOnlineAppointmentMessagePreview
} from "@/constants/onlineAppointmentMessages";

function createTemplateState(templateConfig) {
  return {
    mode: ONLINE_APPOINTMENT_MESSAGE_MODES.PUBLIC,
    content: templateConfig.defaultContent,
    defaultContent: templateConfig.defaultContent,
    savedCustomContent: templateConfig.defaultContent,
    requiredTokens: [],
    supportedTokens: [],
    backendPreview: '',
    backendCharacterCount: '',
    backendSmsSegments: '',
    canEditCustomized: true,
    blockCode: '',
    contentError: '',
    successMessage: '',
    isDirty: false,
    original: {
      mode: ONLINE_APPOINTMENT_MESSAGE_MODES.PUBLIC,
      content: templateConfig.defaultContent,
    },
  }
}

function normalizeCanEditCustomized(value) {
  return ![false, 0, '0', 'false'].includes(value)
}

function resolveTokenList(data, key, fallback = []) {
  if (!Object.prototype.hasOwnProperty.call(data, key)) return fallback
  return Array.isArray(data[key]) ? data[key] : []
}

function normalizeRequiredTokens(templateKey, tokens = []) {
  if (templateKey === ONLINE_APPOINTMENT_TEMPLATE_KEYS.SUBMITTED) {
    return tokens.filter(token => token !== '#appointment_url#')
  }

  return tokens
}

export default {
  name: "OnlineReserveSubmitMessageSetting",

  components: {
    OnlineAppointmentMessageTemplatePanel,
  },

  data() {
    return {
      loading: false,
      quotaDialog: false,
      quotaDialogMessage: '您目前沒有可使用的簡訊點數，無法啟用客製化訊息。請先儲值簡訊點數。',
      savingKey: '',
      payerSaving: false,
      payerSuccessMessage: '',
      payerType: '1',
      originalPayerType: '1',
      activePanel: 0,
      templateConfigs: ONLINE_APPOINTMENT_MESSAGE_TEMPLATES,
      maxCharacters: MAX_ONLINE_APPOINTMENT_MESSAGE_CHARACTERS,
      templateStates: ONLINE_APPOINTMENT_MESSAGE_TEMPLATES.reduce((states, templateConfig) => {
        states[templateConfig.key] = createTemplateState(templateConfig)
        return states
      }, {}),
    }
  },

  computed: {
    ...mapGetters([
      'axiosParamData',
      'userInfo'
    ]),
    previewClinicName() {
      return this.userInfo?.clinic_name || this.userInfo?.name || '品橘牙醫診所'
    },
    isPayerDirty() {
      return this.payerType !== this.originalPayerType
    },
    hasDirtyTemplate() {
      return this.templateConfigs.some(templateConfig => this.templateStates[templateConfig.key]?.isDirty)
    },
  },

  mounted() {
    this.init()
  },

  methods: {
    async init() {
      this.loading = true
      try {
        await this.fetchAllTemplates()
      } catch (e) {
        console.error(e)
        alert('取得客製化訊息設定失敗')
      } finally {
        this.loading = false
      }
    },
    async fetchAllTemplates() {
      await Promise.all(this.templateConfigs.map(templateConfig => this.fetchTemplate(templateConfig)))
    },
    async fetchTemplate(templateConfig) {
      const res = await getOnlineAppointmentTemplateAPI({
        ...this.axiosParamData,
        template_key: templateConfig.key,
      })
      this.applyTemplateData(templateConfig, res.data)
    },
    applyTemplateData(templateConfig, data = {}) {
      const currentState = this.templateStates[templateConfig.key] || createTemplateState(templateConfig)
      const clinicSetting = data.clinic_setting || {}
      const defaultContent = data.default_template?.content || templateConfig.defaultContent
      const rawMode = clinicSetting.mode !== undefined && clinicSetting.mode !== null
        ? clinicSetting.mode
        : data.default_template?.mode
      const mode = this.normalizeMode(rawMode)
      const savedCustomContent = clinicSetting.content || defaultContent
      const content = mode === ONLINE_APPOINTMENT_MESSAGE_MODES.CUSTOM ? savedCustomContent : savedCustomContent

      this.applyPayerData(clinicSetting)

      this.$set(this.templateStates, templateConfig.key, {
        mode,
        content,
        defaultContent,
        savedCustomContent,
        requiredTokens: normalizeRequiredTokens(
          templateConfig.key,
          resolveTokenList(data, 'required_tokens', currentState.requiredTokens)
        ),
        supportedTokens: resolveTokenList(data, 'supported_tokens', currentState.supportedTokens),
        backendPreview: clinicSetting.preview || clinicSetting.effective_content || '',
        backendCharacterCount: clinicSetting.character_count || '',
        backendSmsSegments: clinicSetting.sms_segments || '',
        canEditCustomized: normalizeCanEditCustomized(clinicSetting.can_edit_customized),
        blockCode: clinicSetting.customized_block_code || clinicSetting.block_code || '',
        contentError: '',
        successMessage: '',
        isDirty: false,
        original: {
          mode,
          content,
        },
      })
    },
    applyPayerData(clinicSetting = {}) {
      if (clinicSetting.online_appointment_sms_payer_type === undefined || clinicSetting.online_appointment_sms_payer_type === null) return
      if (this.isPayerDirty) return

      const payerType = this.normalizePayerType(clinicSetting.online_appointment_sms_payer_type)
      this.payerType = payerType
      this.originalPayerType = payerType
    },
    normalizeMode(mode) {
      return String(mode) === ONLINE_APPOINTMENT_MESSAGE_MODES.CUSTOM
        ? ONLINE_APPOINTMENT_MESSAGE_MODES.CUSTOM
        : ONLINE_APPOINTMENT_MESSAGE_MODES.PUBLIC
    },
    normalizePayerType(payerType) {
      return String(payerType) === '2' ? '2' : '1'
    },
    updateContent(templateKey, content) {
      const state = this.templateStates[templateKey]
      state.content = content
      state.contentError = ''
      state.successMessage = ''
      this.markDirty(templateKey)
    },
    handleSwitchMode(templateKey) {
      const state = this.templateStates[templateKey]
      state.successMessage = ''
      state.contentError = ''

      if (state.mode === ONLINE_APPOINTMENT_MESSAGE_MODES.CUSTOM) {
        state.mode = ONLINE_APPOINTMENT_MESSAGE_MODES.PUBLIC
        this.markDirty(templateKey)
        return
      }

      if (!this.canUseTemplateCustomMessage(state)) {
        this.openQuotaDialog(state)
        return
      }

      state.mode = ONLINE_APPOINTMENT_MESSAGE_MODES.CUSTOM
      state.content = state.savedCustomContent || state.defaultContent || this.getTemplateConfig(templateKey).defaultContent
      this.markDirty(templateKey)
    },
    handleEditorClick(templateKey) {
      const state = this.templateStates[templateKey]
      if (state.mode === ONLINE_APPOINTMENT_MESSAGE_MODES.CUSTOM) return
      if (!this.canUseTemplateCustomMessage(state)) this.openQuotaDialog(state)
    },
    canUseTemplateCustomMessage(state) {
      return state.canEditCustomized !== false
    },
    openQuotaDialog(state) {
      this.quotaDialogMessage = state.blockCode === 'SMS_QUOTA_REQUIRED'
        ? '您目前尚未儲值簡訊點數，無法啟用客製化訊息。請先儲值簡訊點數。'
        : '您目前沒有可使用的簡訊點數，無法啟用客製化訊息。請先儲值簡訊點數。'
      this.quotaDialog = true
    },
    markDirty(templateKey) {
      const state = this.templateStates[templateKey]
      state.isDirty = state.mode !== state.original.mode ||
        (state.mode === ONLINE_APPOINTMENT_MESSAGE_MODES.CUSTOM && (state.content || '') !== (state.original.content || ''))
    },
    validateContent(templateKey) {
      const state = this.templateStates[templateKey]
      if (state.mode !== ONLINE_APPOINTMENT_MESSAGE_MODES.CUSTOM) {
        state.contentError = ''
        return true
      }

      const content = state.content || ''
      const missingTokens = state.requiredTokens.filter(token => !content.includes(token))
      if (missingTokens.length) {
        state.contentError = `訊息內容必須包含「${missingTokens.map(getOnlineAppointmentTokenLabel).join('」與「')}」`
        return false
      }

      const previewContent = renderOnlineAppointmentMessagePreview(content, this.previewClinicName)
      if (previewContent.length > this.maxCharacters) {
        state.contentError = `訊息內容最多 ${this.maxCharacters} 字`
        return false
      }

      state.contentError = ''
      return true
    },
    async saveSetting(templateKey) {
      const state = this.templateStates[templateKey]
      state.successMessage = ''

      if (state.mode === ONLINE_APPOINTMENT_MESSAGE_MODES.CUSTOM && !this.canUseTemplateCustomMessage(state)) {
        this.openQuotaDialog(state)
        return
      }

      if (!this.validateContent(templateKey)) return

      this.savingKey = templateKey
      try {
        const res = await updateOnlineAppointmentTemplateBulkAPI({
          ...this.axiosParamData,
          template_key: templateKey,
          mode: state.mode,
          content: state.mode === ONLINE_APPOINTMENT_MESSAGE_MODES.CUSTOM ? state.content : null,
        })
        const templateConfig = this.getTemplateConfig(templateKey)
        this.applyTemplateData(templateConfig, res.data)
        this.templateStates[templateKey].successMessage = '設定已儲存。'
      } catch (e) {
        this.handleSaveError(templateKey, e)
      } finally {
        this.savingKey = ''
      }
    },
    async savePayerSetting() {
      this.payerSuccessMessage = ''
      if (this.hasDirtyTemplate) {
        alert('請先儲存或取消正在編輯的訊息內容，再儲存付款方設定')
        return
      }

      this.payerSaving = true
      let payerSaved = false
      try {
        const res = await updateOnlineAppointmentTemplateBulkAPI({
          ...this.axiosParamData,
          online_appointment_sms_payer_type: this.payerType,
        })
        payerSaved = true
        const savedPayerType = this.normalizePayerType(
          res.data?.clinic_setting?.online_appointment_sms_payer_type || this.payerType
        )
        this.payerType = savedPayerType
        this.originalPayerType = savedPayerType
        await this.fetchAllTemplates()
        this.payerSuccessMessage = '付款方設定已儲存。'
      } catch (e) {
        if (payerSaved) {
          console.error(e)
          alert('付款方設定已儲存，但重新取得客製化訊息設定失敗，請重新整理頁面')
          return
        }

        const response = e.response?.data || {}
        const failCode = response.fail_code || response.code
        if (failCode === 'INVALID_ONLINE_APPOINTMENT_SMS_PAYER_TYPE') {
          alert('付款方設定格式不正確')
          return
        }

        console.error(e)
        alert('儲存付款方設定失敗')
      } finally {
        this.payerSaving = false
      }
    },
    handleSaveError(templateKey, e) {
      const response = e.response?.data || {}
      const failCode = response.fail_code || response.code
      const state = this.templateStates[templateKey]

      if (failCode === 'SMS_QUOTA_REQUIRED') {
        this.openQuotaDialog({
          ...state,
          blockCode: failCode,
        })
        return
      }

      if (failCode === 'INVALID_ONLINE_APPOINTMENT_TEMPLATE') {
        const missingTokens = normalizeRequiredTokens(templateKey, response.missing_tokens || [])
        state.contentError = missingTokens.length
          ? `訊息內容必須包含「${missingTokens.map(getOnlineAppointmentTokenLabel).join('」與「')}」`
          : '訊息內容格式不正確'
        return
      }

      console.error(e)
      alert('儲存客製化訊息設定失敗')
    },
    cancel(templateKey) {
      const state = this.templateStates[templateKey]
      state.mode = state.original.mode
      state.content = state.original.content
      state.contentError = ''
      state.successMessage = ''
      state.isDirty = false
    },
    getTemplateConfig(templateKey) {
      return this.templateConfigs.find(item => item.key === templateKey)
    },
  },
}
</script>
